import {
  Bookmark,
  Columns2,
  Copy,
  Download,
  Eye,
  FilePlus2,
  FolderOpen,
  Keyboard,
  Link,
  MousePointerClick,
  Palette,
  Redo2,
  Save,
  ScanEye,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { VISION_MODES } from "@/color/cvd";
import { isDirty, useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import { Button } from "@/ui/kit/button";
import { cn } from "@/ui/kit/cn";
import { Kbd } from "@/ui/kit/kbd";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "@/ui/kit/menu";
import { Tip } from "@/ui/kit/tooltip";
import {
  backup,
  importFile,
  pickFile,
  pinCurrentAsB,
  save,
  saveAs,
  share,
  snapshot,
} from "../actions";

function Logo() {
  return (
    <div className="flex items-center gap-2 pr-1">
      <svg viewBox="0 0 32 32" className="size-6" aria-hidden="true">
        <defs>
          <linearGradient id="logo-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#fb4934" />
            <stop offset=".35" stopColor="#fabd2f" />
            <stop offset=".65" stopColor="#83a598" />
            <stop offset="1" stopColor="#d3869b" />
          </linearGradient>
        </defs>
        <circle cx="16" cy="16" r="11" fill="url(#logo-g)" />
        <circle cx="16" cy="16" r="5" fill="var(--chrome-bg)" />
      </svg>
      <span className="font-semibold tracking-tight">themes</span>
    </div>
  );
}

function ThemeName() {
  const name = useEditor((s) => s.draft.name);
  const variant = useEditor((s) => s.draft.variant);
  const dirty = useEditor((s) => isDirty(s));
  const edit = useEditor((s) => s.edit);
  const [text, setText] = useState(name);
  useEffect(() => setText(name), [name]);
  const commit = () => {
    const next = text.trim();
    if (next && next !== name) edit((t) => ({ ...t, name: next.slice(0, 80) }));
    else setText(name);
  };
  return (
    <div className="flex min-w-0 items-center gap-2">
      <input
        aria-label="Theme name"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setText(name);
        }}
        size={Math.max(8, text.length + 1)}
        className="min-w-0 rounded-md border border-transparent bg-transparent px-1.5 py-1 font-medium text-sm outline-none hover:border-line focus:border-accent focus:bg-bg"
      />
      <span className="rounded border border-line px-1.5 py-px text-[10px] text-muted">
        {variant}
      </span>
      {dirty && (
        <Tip label="Unsaved changes" keys="Ctrl S">
          <span className="size-1.5 rounded-full bg-warn" />
        </Tip>
      )}
    </div>
  );
}

function ABControls() {
  const compare = useEditor((s) => s.compare);
  const showCompare = useEditor((s) => s.showCompare);
  const toggle = useEditor((s) => s.toggleCompare);
  const setCompare = useEditor((s) => s.setCompare);
  if (!compare) {
    return (
      <Tip label="Pin the current state as B, keep editing A, then flip with \">
        <Button size="sm" variant="ghost" onClick={pinCurrentAsB}>
          <Columns2 /> Pin B
        </Button>
      </Tip>
    );
  }
  return (
    <div className="flex items-center rounded-md border border-line bg-panel p-0.5">
      <button
        type="button"
        onClick={() => toggle(false)}
        className={cn(
          "h-6 rounded px-2 font-semibold text-xs",
          !showCompare ? "bg-raised text-fg" : "text-muted",
        )}
      >
        A
      </button>
      <Tip label={<span>B: {compare.label}</span>} keys="\">
        <button
          type="button"
          onClick={() => toggle(true)}
          className={cn(
            "h-6 max-w-40 truncate rounded px-2 font-semibold text-xs",
            showCompare ? "bg-accent text-accent-fg" : "text-muted",
          )}
          data-testid="ab-b"
        >
          B
        </button>
      </Tip>
      <Tip label="Unpin B">
        <button
          type="button"
          onClick={() => setCompare(null)}
          className="ml-0.5 rounded p-1 text-faint hover:text-fg"
          aria-label="Unpin B"
        >
          <X className="size-3" />
        </button>
      </Tip>
    </div>
  );
}

export function TopBar() {
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);
  const canUndo = useEditor((s) => s.past.length > 0);
  const canRedo = useEditor((s) => s.future.length > 0);
  const vision = useEditor((s) => s.vision);
  const inspect = useEditor((s) => s.inspect);
  const dogfood = useEditor((s) => s.dogfood);
  const set = useEditor((s) => s.set);
  const openDialog = useUi((s) => s.openDialog);
  const setPalette = useUi((s) => s.setPalette);

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-line border-b bg-panel px-3">
      <Logo />
      <div className="mx-1 h-5 w-px bg-line" />
      <Menu>
        <MenuTrigger asChild>
          <Button size="sm" variant="ghost" aria-label="Preset menu">
            <FolderOpen /> Presets
          </Button>
        </MenuTrigger>
        <MenuContent>
          <MenuItem icon={<FolderOpen />} keys="Ctrl O" onSelect={() => openDialog("presets")}>
            Open preset or starter…
          </MenuItem>
          <MenuItem icon={<Save />} keys="Ctrl S" onSelect={save}>
            Save
          </MenuItem>
          <MenuItem icon={<FilePlus2 />} keys="Ctrl ⇧ S" onSelect={saveAs}>
            Save as copy
          </MenuItem>
          <MenuItem icon={<Bookmark />} onSelect={() => snapshot()}>
            Take snapshot
          </MenuItem>
          <MenuSeparator />
          <MenuItem
            icon={<Upload />}
            onSelect={async () => {
              const f = await pickFile();
              if (f) await importFile(f);
            }}
          >
            Import theme file…
          </MenuItem>
          <MenuItem icon={<Copy />} onSelect={backup}>
            Back up all presets
          </MenuItem>
        </MenuContent>
      </Menu>
      <ThemeName />
      <span className="flex-1" />
      <Tip label="Undo" keys="Ctrl Z">
        <Button size="icon-sm" variant="ghost" disabled={!canUndo} onClick={undo} aria-label="Undo">
          <Undo2 />
        </Button>
      </Tip>
      <Tip label="Redo" keys="Ctrl ⇧ Z">
        <Button size="icon-sm" variant="ghost" disabled={!canRedo} onClick={redo} aria-label="Redo">
          <Redo2 />
        </Button>
      </Tip>
      <div className="mx-1 h-5 w-px bg-line" />
      <ABControls />
      <Tip label="Inspect: hover the preview to see tokens, click to edit" keys="I">
        <Button
          size="icon-sm"
          variant={inspect ? "default" : "ghost"}
          className={cn(inspect && "text-accent")}
          onClick={() => set({ inspect: !inspect })}
          aria-label="Inspect"
          aria-pressed={inspect}
        >
          <MousePointerClick />
        </Button>
      </Tip>
      <Menu>
        <Tip label="Color-vision simulation">
          <MenuTrigger asChild>
            <Button
              size="icon-sm"
              variant={vision !== "normal" ? "default" : "ghost"}
              className={cn(vision !== "normal" && "text-accent")}
              aria-label="Vision simulation"
            >
              <Eye />
            </Button>
          </MenuTrigger>
        </Tip>
        <MenuContent align="end">
          <MenuLabel>Simulate in previews</MenuLabel>
          {VISION_MODES.map((m) => (
            <MenuItem
              key={m.id}
              icon={vision === m.id ? <ScanEye /> : null}
              onSelect={() => set({ vision: m.id })}
            >
              <div>{m.label}</div>
              <div className="text-[11px] text-faint">{m.hint}</div>
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
      <Tip label="Dogfood: theme this app with your palette">
        <Button
          size="icon-sm"
          variant={dogfood ? "default" : "ghost"}
          className={cn(dogfood && "text-accent")}
          onClick={() => set({ dogfood: !dogfood })}
          aria-label="Dogfood"
          aria-pressed={dogfood}
        >
          <Palette />
        </Button>
      </Tip>
      <Tip label="Keyboard shortcuts" keys="?">
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={() => openDialog("shortcuts")}
          aria-label="Shortcuts"
        >
          <Keyboard />
        </Button>
      </Tip>
      <div className="mx-1 h-5 w-px bg-line" />
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setPalette(true)}
        className="hidden text-faint lg:inline-flex"
      >
        Commands <Kbd>Ctrl K</Kbd>
      </Button>
      <Tip label="Copy a share link (the theme lives in the URL)">
        <Button size="sm" variant="outline" onClick={share}>
          <Link /> Share
        </Button>
      </Tip>
      <Button
        size="sm"
        variant="primary"
        onClick={() => openDialog("export")}
        data-testid="open-export"
      >
        <Download /> Export
      </Button>
    </header>
  );
}
